<template>
  <div class="reconciliation">
    <q-linear-progress v-if="isFetching" indeterminate />

    <div class="reconciliation__filters">
      <CustomDate
        :value="filters.start_date"
        label="Период с"
        placeholder="Выберите дату"
        :required="true"
        :triggerValidationOnMount="triggerValidation"
        :minWidthMode="true"
        @change_date="(value) => onChangeDate('start_date', value)"
      />
      <CustomDate
        :value="filters.end_date"
        label="Период по"
        placeholder="Выберите дату"
        :required="true"
        :triggerValidationOnMount="triggerValidation"
        :minWidthMode="true"
        @change_date="(value) => onChangeDate('end_date', value)"
      />
      <q-btn
        color="primary"
        unelevated
        :loading="isFetching"
        class="reconciliation__btn"
        @click="handleFetch"
      >
        Получить акт сверки
      </q-btn>
    </div>

    <NotFoundText
      v-if="!tableRows.length && !isFetching"
      :text="emptyText"
    />

    <div v-if="tableRows.length" class="reconciliation__content">
      <div class="reconciliation__header-info">
        {{ headerInfo }}
      </div>

      <CustomTable
        title="Акт сверки"
        :rowsData="tableRows"
        :columns="columns"
        :stickyHeader="false"
        :loading="isFetching"
        :custom-body="true"
        :small="true"
      >
        <template #customBody="props">
          <q-tr>
            <custom-table-cell align="center" vertical-align="center">
              {{ formatDate(props.row.date) }}
            </custom-table-cell>
            <custom-table-cell align="left" vertical-align="center">
              <span :class="props.row._isBold ? 'text-bold' : ''">
                {{ props.row.document }}
              </span>
            </custom-table-cell>
            <custom-table-cell
              align="right"
              vertical-align="center"
            >
              <Amount
                v-if="props.row.debit !== null && props.row.debit !== undefined"
                :value="props.row.debit"
                :class="props.row._isBold ? 'text-bold' : ''"
              />
            </custom-table-cell>
            <custom-table-cell
              align="right"
              vertical-align="center"
            >
              <Amount
                v-if="props.row.credit !== null && props.row.credit !== undefined"
                :value="props.row.credit"
                :class="props.row._isBold ? 'text-bold' : ''"
              />
            </custom-table-cell>
          </q-tr>
        </template>
      </CustomTable>

    </div>
  </div>
</template>

<script>
import Amount from "@/components/common/Amount.vue";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import NotFoundText from "@/components/common/NotFoundText.vue";
import CustomDate from "@/components/form/CustomDate";
import { showErrorToast } from "@/services";
import { getProjectReconciliation } from "./services";

export default {
  name: "Reconciliation",
  props: ["params", "projectId"],
  components: {
    CustomTable,
    CustomTableCell,
    CustomDate,
    Amount,
    NotFoundText,
  },
  data() {
    return {
      isFetching: false,
      hasTriedLoad: false,
      headerInfo: "",
      filters: {
        start_date: "",
        end_date: "",
      },
      triggerValidation: false,
      columns: [
        { name: "date", label: "Дата", field: "date", align: "center" },
        {
          name: "document",
          label: "Документ",
          field: "document",
          align: "left",
        },
        {
          name: "debit",
          label: "Дебет, тг",
          field: "debit",
          align: "right",
        },
        {
          name: "credit",
          label: "Кредит, тг",
          field: "credit",
          align: "right",
        },
      ],
      tableRows: [],
      remainingStartRaw: 0,
      remainingEndRaw: 0,
    };
  },
  computed: {
    startBalance() {
      return this.splitBalance(this.remainingStartRaw);
    },
    endBalance() {
      return this.splitBalance(this.remainingEndRaw);
    },
    emptyText() {
      if (!this.hasTriedLoad) {
        return "Укажите период и нажмите «Получить акт сверки»";
      }
      if (!this.filters.start_date || !this.filters.end_date) {
        return "Для формирования акта выберите обе даты периода и нажмите кнопку выше.";
      }
      return "За выбранный период данных нет.";
    },
  },
  methods: {
    onChangeDate(key, value) {
      this.filters[key] = value;
    },
    formatDate(value) {
      if (!value) return "";
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleDateString("ru");
      } catch (e) {
        return value;
      }
    },
    splitBalance(value) {
      const num = Number(value || 0);
      if (!num) {
        return { debit: 0, credit: 0 };
      }
      if (num > 0) {
        return { debit: Math.abs(num), credit: 0 };
      }
      return { debit: 0, credit: Math.abs(num) };
    },
    buildStartBalanceRow() {
      const { debit, credit } = this.startBalance;
      return {
        _type: "start_balance",
        _isBold: true,
        document: `Остаток на начало ${this.filters.start_date || "-"}`,
        date: null,
        // Если сумм нет — показываем 0 в кредите
        debit: debit ? debit : null,
        credit: debit || credit ? credit || null : 0,
      };
    },
    buildTotalRow(reports) {
      const debitSum = reports.reduce(
        (acc, item) => acc + (Number(item.debit) || 0),
        0
      );
      const creditSum = reports.reduce(
        (acc, item) => acc + (Number(item.credit) || 0),
        0
      );
      return {
        _type: "total",
        _isBold: true,
        document: "Итого",
        date: null,
        debit: debitSum,
        credit: creditSum,
      };
    },
    buildEndBalanceRow() {
      const { debit, credit } = this.endBalance;
      return {
        _type: "end_balance",
        _isBold: true,
        document: `Остаток на конец ${this.filters.end_date || "-"}`,
        date: null,
        // Если сумм нет — показываем 0 в кредите
        debit: debit ? debit : null,
        credit: debit || credit ? credit || null : 0,
      };
    },
    mapReportRow(item) {
      return {
        ...item,
        date: item.date,
        document: item.document,
        debit: Math.abs(Number(item.debit) || 0) || null,
        credit: Math.abs(Number(item.credit) || 0) || null,
      };
    },
    async handleFetch() {
      this.triggerValidation = true;
      this.hasTriedLoad = true;

      if (!this.filters.start_date || !this.filters.end_date) {
        showErrorToast("Для формирования акта выберите обе даты периода (с и по).");
        return;
      }
      if (!this.projectId) {
        showErrorToast("Не выбран проект");
        return;
      }

      this.isFetching = true;
      try {
        const body = {
          project_id: Number(this.projectId),
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
        };
        const res = await getProjectReconciliation(body);
        this.isFetching = false;

        if (!res) {
          this.tableRows = [];
          this.headerInfo = "";
          this.remainingStartRaw = 0;
          this.remainingEndRaw = 0;
          return;
        }

        this.headerInfo = res.header_info || "";
        this.remainingStartRaw = Number(res.remaining_start || 0);
        this.remainingEndRaw = Number(res.remaining_end || 0);

        const reports = Array.isArray(res.reports) ? res.reports : [];
        const mappedReports = reports.map((r) => this.mapReportRow(r));
        const rows = [];

        // Остаток на начало (строка всегда есть, даже если суммы 0)
        rows.push(this.buildStartBalanceRow());

        // Операции
        rows.push(...mappedReports);

        // Итого
        if (mappedReports.length) {
          rows.push(this.buildTotalRow(mappedReports));
        }

        // Остаток на конец (строка всегда есть, даже если суммы 0)
        rows.push(this.buildEndBalanceRow());

        this.tableRows = rows;
      } catch (e) {
        this.isFetching = false;
        showErrorToast("Не удалось загрузить акт сверки");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.reconciliation {
  padding: 10px 0 0;

  &__filters {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }

  &__btn {
    height: 40px;
  }

  &__content {
    margin-top: 10px;
  }

  &__header-info {
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
  }

}
</style>


